'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

interface ContactFormData {
  fullName: string;
  email: string;
  phone: string;
  subject: string;
  propertyRef: string;
  message: string;
  urgency: string;
}

const managers = [
{
  id: 'mgr-001',
  name: 'Jonathan Reeves',
  title: 'Senior Property Manager',
  image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face',
  properties: 28,
  responseTime: '< 2 hours',
  rating: 4.8,
  available: true
},
{
  id: 'mgr-002',
  name: 'Diana Osei',
  title: 'Hospitality Services Manager',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12b96fde1-1763296237023.png",
  properties: 15,
  responseTime: '< 4 hours',
  rating: 4.9,
  available: true
},
{
  id: 'mgr-003',
  name: 'Marcus Chen',
  title: 'Operations Manager',
  image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face',
  properties: 21,
  responseTime: '< 1 hour',
  rating: 4.7,
  available: false
}];


export default function ManagerContactCard() {
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactFormData>();

  const onSubmit = async (data: ContactFormData) => {
    setSubmitting(true);
    // TODO: POST /api/contact-manager with data
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    toast.success('Message sent! Your manager will respond within the stated timeframe.');
    reset();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Manager profiles */}
      <div className="lg:col-span-1">
        <h3 className="font-display font-semibold text-lg text-foreground mb-4">Available Managers</h3>
        <div className="flex flex-col gap-4">
          {managers.map((mgr) =>
          <div key={mgr.id} className="bg-white rounded-xl border border-border p-4 shadow-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="relative">
                  <img
                  src={mgr.image}
                  alt={`${mgr.name} profile photo`}
                  className="w-12 h-12 rounded-full object-cover" />

                  <span
                  className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${
                  mgr.available ? 'bg-emerald-500' : 'bg-gray-400'}`
                  } />

                </div>
                <div>
                  <p className="font-display font-semibold text-sm text-foreground">{mgr.name}</p>
                  <p className="text-xs text-muted-foreground">{mgr.title}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-muted rounded-lg p-2 text-center">
                  <p className="text-muted-foreground">Properties</p>
                  <p className="font-semibold text-foreground">{mgr.properties}</p>
                </div>
                <div className="bg-muted rounded-lg p-2 text-center">
                  <p className="text-muted-foreground">Response</p>
                  <p className="font-semibold text-foreground">{mgr.responseTime}</p>
                </div>
              </div>
              <div className={`mt-2 text-center text-xs font-medium py-1 rounded-lg ${
            mgr.available ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'}`
            }>
                {mgr.available ? '● Online now' : '○ Currently away'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contact form */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-xl border border-border p-6 shadow-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-primary/10 rounded-xl">
              <Icon name="ChatBubbleLeftRightIcon" size={22} className="text-primary" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg text-foreground">Contact a Manager</h3>
              <p className="text-sm text-muted-foreground">We typically respond within 2–4 hours</p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Full Name <span className="text-destructive">*</span>
                </label>
                <input
                  {...register('fullName', { required: 'Full name is required' })}
                  type="text"
                  placeholder="Your full name"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
                  errors.fullName ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'}`
                  } />

                {errors.fullName && <p className="text-destructive text-xs mt-1">{errors.fullName.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Email Address <span className="text-destructive">*</span>
                </label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email address' }
                  })}
                  type="email"
                  placeholder="your@email.com"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
                  errors.email ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'}`
                  } />

                {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Phone Number</label>
                <input
                  {...register('phone')}
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  className="w-full px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-primary transition-colors" />

              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Urgency Level <span className="text-destructive">*</span>
                </label>
                <select
                  {...register('urgency', { required: 'Please select urgency' })}
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors bg-white cursor-pointer ${
                  errors.urgency ? 'border-destructive' : 'border-border focus:border-primary'}`
                  }>

                  <option value="">Select urgency</option>
                  <option value="low">Low — General inquiry</option>
                  <option value="medium">Medium — Within 24 hours</option>
                  <option value="high">High — Within 4 hours</option>
                  <option value="urgent">Urgent — Emergency</option>
                </select>
                {errors.urgency && <p className="text-destructive text-xs mt-1">{errors.urgency.message}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Subject <span className="text-destructive">*</span>
              </label>
              <input
                {...register('subject', { required: 'Subject is required' })}
                type="text"
                placeholder="Brief description of your inquiry"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
                errors.subject ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'}`
                } />

              {errors.subject && <p className="text-destructive text-xs mt-1">{errors.subject.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Property / Booking Reference</label>
              <p className="text-xs text-muted-foreground mb-1">If you have a booking or property reference number, enter it here</p>
              <input
                {...register('propertyRef')}
                type="text"
                placeholder="e.g. APT-2024-001 or BKG-0042"
                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-primary transition-colors" />

            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Message <span className="text-destructive">*</span>
              </label>
              <textarea
                {...register('message', { required: 'Message is required', minLength: { value: 20, message: 'Message must be at least 20 characters' } })}
                rows={4}
                placeholder="Describe your request or concern in detail..."
                className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors resize-none ${
                errors.message ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'}`
                } />

              {errors.message && <p className="text-destructive text-xs mt-1">{errors.message.message}</p>}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-primary text-white font-semibold text-sm rounded-lg hover:bg-primary-light transition-all duration-150 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ minHeight: '44px' }}>

              {submitting ?
              <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending message...
                </> :

              <>
                  <Icon name="PaperAirplaneIcon" size={16} />
                  Send Message to Manager
                </>
              }
            </button>
          </form>
        </div>
      </div>
    </div>);

}